import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import { useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react'
import classNames from 'classnames'
import {
  useLayout,
  useQueryState,
  useResponsive,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import useCleanRouter from '@dustin-web/microsite-components/src/hooks/use-clean-router'
import { ProductList, ProductListOptions, getSortBy, useProductListSettings } from '@dustin-web/product-components/src/product-list'
import { FilterBox, Filters } from '@dustin-web/product-components/src/filters'
import { ActiveFilters } from '@dustin-web/product-components/src/filters/active-filters'
import { Drawer } from '@dustin-web/microsite-components/src/drawer'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
import Button from '@dustin-web/microsite-components/src/button'
import InfinityScrollButton from '@dustin-web/microsite-components/src/infinity-scroll-button'
import CategoryNavigationLinkAll from '@dustin-web/product-components/src/category-navigation-link-all'
import SearchNavigationItemList from '@dustin-web/product-components/src/search-navigation/search-navigation-list'
import { ScrollToResults } from '../filters/scroll-to-results'
import { useInfinityScroll, infinityScrollPageSize } from '../utils/infinity-scroll/use-infinity-scroll'
import { PopoverInitializer } from '../utils'
import { SearchHeader } from './search-header'
import { useMicrositeSettingsContext } from '@dustin-web/microsite-components/src/hooks/use-microsite-settings'
import { dangerously_getGraphqlFetcher } from '@dustin-web/microsite-data/src/hooks/graphql-fetcher'
import { SearchPage as SearchPageQuery_GQL } from '@productsite/src/graphql/search.graphql'
import type {
  ProductReferenceFragment,
  SearchPageQuery,
  QuerySearchPageArgs,
} from '@productsite/src/graphql/generated/graphql-types'
import { Origin, Touchpoint } from '@productsite/src/graphql/generated/graphql-types'
import type { FacetGroup } from '../filters'

const SearchSecondaryList = dynamic(() => import('./search-secondary-list/'))

type Props = {
  searchPage: SearchPageQuery['searchPage']
  loading: boolean
  searchPhrase: string
  selectedCategoryPath: string | null
  isUserInitiated: boolean
}

const SearchPage = ({
  searchPage,
  loading,
  searchPhrase,
  selectedCategoryPath,
  isUserInitiated,
}: Props) => {
  const [showMobileFilter, setShowMobileFilter] = useState(false)
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  const { translate } = useTranslations()
  const { queryState, queryString, setQueryState } = useQueryState()
  const { responsive } = useLayout()
  const { query } = useCleanRouter()
  const { breakpoints } = useResponsive()
  const pathname = usePathname()

  const activeCategoryPath = !selectedCategoryPath ? '/' : selectedCategoryPath

  const {
    facets,
    hits,
    autoCorrect,
    secondaryList,
    navigationTree,
    relatedSearches,
    productReferences: products,
  } = searchPage

  const encodedSearchPhraseQuery = encodeURIComponent(autoCorrect?.q || searchPhrase)
  const decodedSearchPhraseQuery = decodeURIComponent(autoCorrect?.q || searchPhrase)

  if (typeof window !== 'undefined' && autoCorrect?.q && pathname) {
    const updatedUrl = pathname.replace(/\/([^/]+)\/[^/]+$/, `/$1/${autoCorrect.q}`)
    window.history.replaceState(window.history.state, '', updatedUrl)
  }

  const { productsToShow: infiniteProductsScroll, addPreviousPage } = useInfinityScroll({
    products: products as ProductReferenceFragment[],
  })

  // Fetch previous page on-demand when user scrolls to the top sentinel
  const micrositeSettings = useMicrositeSettingsContext()
  const { sortBy } = useProductListSettings()
  const fetcher = useMemo(
    () => dangerously_getGraphqlFetcher(micrositeSettings, '', {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const topSentinelRef = useRef<HTMLDivElement | null>(null)
  const [initialPage] = useState(queryState.page)
  const isPrependingRef = useRef(false)

  // Background fetch: fetch previous pages one by one starting from page load
  const bufferedPagesRef = useRef<{ pageInUrl: number; products: ProductReferenceFragment[] }[]>([])
  const [hasBufferedPages, setHasBufferedPages] = useState(false)

  useEffect(() => {
    if (initialPage <= 1) return
    let isCancelled = false

    const fetchPreviousPages = async () => {
      for (let p = initialPage - 1; p >= 1; p--) {
        if (isCancelled) break
        try {
          const result = await fetcher.fetchGraphql<SearchPageQuery, QuerySearchPageArgs>(
            SearchPageQuery_GQL,
            {
              isUserInitiated,
              page: p,
              pageSize: infinityScrollPageSize,
              searchPhrase: decodedSearchPhraseQuery,
              selectedCategoryPath,
              origin: Origin.Organic,
              touchpoint: (responsive.isSmallScreen && Touchpoint.Mobile) || Touchpoint.Desktop,
              facets: queryState.activeFilters,
              sortBy: getSortBy(sortBy),
            }
          )
          if (isCancelled) break
          const pageProducts = (result?.searchPage?.productReferences ?? []) as ProductReferenceFragment[]
          if (pageProducts.length > 0) {
            bufferedPagesRef.current.push({ pageInUrl: p, products: pageProducts })
            setHasBufferedPages(true)
          }
        } catch (err) {
          console.error(`Error fetching page ${p}`, err)
          break
        }
      }
    }

    fetchPreviousPages()
    return () => { isCancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reveal buffered pages one at a time when user scrolls to top sentinel
  useEffect(() => {
    const sentinel = topSentinelRef.current
    if (!sentinel || initialPage <= 1 || !hasBufferedPages) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && bufferedPagesRef.current.length > 0) {
          // Take the highest page number (closest to current)
          bufferedPagesRef.current.sort((a, b) => b.pageInUrl - a.pageInUrl)
          const nextPage = bufferedPagesRef.current.shift()!
          isPrependingRef.current = true
          addPreviousPage(nextPage.pageInUrl, nextPage.products)
          if (bufferedPagesRef.current.length === 0) {
            setHasBufferedPages(false)
          }
        }
      },
      { threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [initialPage, hasBufferedPages, addPreviousPage])

  const productsToShow = infiniteProductsScroll?.filter(
    product => product?.searchResultProduct?.availability?.isAvailableForSale
  ) as ProductReferenceFragment[]

  // Adjust scroll position when previous pages are prepended, before browser paint
  const prevProductCountRef = useRef(productsToShow.length)
  const lastScrollHeightRef = useRef(0)

  useLayoutEffect(() => {
    const newProducts = productsToShow.length - prevProductCountRef.current
    if (newProducts > 0 && prevProductCountRef.current > 0 && isPrependingRef.current) {
      // Products were prepended — shift viewport down to keep the same products in view
      const diff = document.documentElement.scrollHeight - lastScrollHeightRef.current
      window.scrollBy({ top: diff, behavior: 'instant' })
      isPrependingRef.current = false
    }
    lastScrollHeightRef.current = document.documentElement.scrollHeight
    prevProductCountRef.current = productsToShow.length
  }, [productsToShow.length])

  const showFilterColumn = facets && facets?.length > 0
  const productListRef = useRef<HTMLDivElement | null>(null)
  const filterRef = useRef<HTMLDivElement | null>(null)
  const viewMoreRef = useRef<HTMLDivElement | null>(null)

  // When landing directly on page > 1, scroll to the bottom of the product list
  useEffect(() => {
    if (initialPage > 1 && viewMoreRef.current) {
      const SCROLL_OFFSET_FOR_BUTTON_VISIBILITY = 300
      const el = viewMoreRef.current
      const buttonBottom = el.offsetTop + el.offsetHeight
      window.scrollTo({ top: buttonBottom - window.innerHeight + SCROLL_OFFSET_FOR_BUTTON_VISIBILITY, behavior: 'instant' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // don't count 'path'
    const filters = queryState.activeFilters.filter(f => f.name !== 'path')
    setHasActiveFilters(filters.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  useEffect(() => PopoverInitializer.init(), [])

  const clearFilters = () => {
    // set all defined keys to null
    const params = Object.fromEntries(queryState.activeFilters.map(f => [f.name, null]))

    setQueryState({
      ...params,
      page: '',
    })
  }

  const searchBase = translate('SearchPath')

  const formatedRelatedSearches = relatedSearches?.map(
    (item: NonNullable<SearchPageQuery['searchPage']['relatedSearches']>[number]) => {
      const title = item?.title ?? ''
      const url = item?.url ?? ''
      const updatedUrl = (pathname ?? '').replace(/\/([^/]+)\/[^/]+$/, `/$1/${url.replaceAll('/', '')}`)
      return { title, url: updatedUrl }
    }
  )

  query?.delete('page')
  const queryWithQuestionMark = query?.toString() ? `?${query}` : ''

  return (
    <div id="page-container" className="c-filter u-pt-16" data-gasource="SearchListing">
      <div className="container u-mb-96">
        <div className={classNames('row sm-gutters', { 'u-mb-48': !!secondaryList?.length })}>
          <SearchHeader
            hits={hits}
            autocorrectedPhrase={autoCorrect?.q}
            phrase={searchPhrase}
            relatedSearches={formatedRelatedSearches!}
          />
          {showFilterColumn && (
            <Drawer
              visible={showMobileFilter && responsive.isSmallScreen}
              onClose={() => setShowMobileFilter(false)}
              returnOnlyChildren={!responsive.isSmallScreen}
              className="row no-gutters u-px-0 u-py-0">
              <div className="col-12 col-lg-3">
                <FilterBox noIcon title={translate('CategoryHeadline')} withPadding>
                  <>
                    <CategoryNavigationLinkAll
                      selectedCategory={activeCategoryPath}
                      linkHref={`/${searchBase}/${encodedSearchPhraseQuery}${queryWithQuestionMark}`}
                    />
                    <SearchNavigationItemList
                      navigationItems={navigationTree!}
                      selectedCategoryPath={activeCategoryPath}
                      buildPublicUrl={navItem =>
                        `/${searchBase}/${encodedSearchPhraseQuery}/${navItem.categoryPathSlug}${queryWithQuestionMark}`
                      }
                      onItemClick={val => DigitalData.pushFilterCatClick(val)}
                    />
                  </>
                </FilterBox>
                {facets.length > 0 && (
                  <>
                    <FilterBox
                      sticky={!responsive.isSmallScreen}
                      stickyOffset={120}
                      ref={filterRef}
                      title={translate('mobilefilterlabel')}
                      additionalTopContent={
                        <Button
                          className="button-stripped p-sm u-mb-0"
                          text={translate('ResetAllFilters')}
                          onClick={clearFilters}
                          defaultBtn
                          disabled={!hasActiveFilters}></Button>
                      }
                      activeFilters={
                        showMobileFilter ? (
                          <ActiveFilters facetGroups={facets as FacetGroup[]} mobile />
                        ) : null
                      }>
                      <Filters
                        facets={facets as FacetGroup[]}
                      />
                    </FilterBox>

                    {responsive.isSmallScreen && (
                      <div
                        className="u-sticky-bottom u-bg-white u-px-16 u-py-16 d-flex"
                        style={{ zIndex: 10 }}>
                        <Button
                          className="c-btn u-mb-0 u-w-shrink"
                          text={translate('ResetAllFilters')}
                          disabled={!hasActiveFilters}
                          onClick={() => clearFilters()}></Button>
                        <Button
                          className="c-btn c-btn--primary u-flex-1 u-mb-0"
                          text={translate(
                            hasActiveFilters
                              ? 'MobileCloseAndShowProductsFilterLabel'
                              : 'MobileCloseLabel',
                            { count: hits }
                          )}
                          onClick={() => setShowMobileFilter(false)}></Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Drawer>
          )}
          <div
            id="filter-product-list"
            className={classNames({
              'col-12 col-lg-9': showFilterColumn,
              'col-12': !showFilterColumn,
            })}>
            {showFilterColumn && (
              <>
                <ProductListOptions
                  disablePageSize
                  disableSearch
                  onMobileFilterOverlayOpen={() => setShowMobileFilter(true)}
                />
                <ActiveFilters facetGroups={facets as FacetGroup[]} onClearFilter={clearFilters} />
              </>
            )}
            <div ref={productListRef} className="u-mb-16">
              {initialPage > 1 && (
                <div ref={topSentinelRef} style={{ height: 1 }} />
              )}
              <ProductList
                productReferences={productsToShow}
                loading={loading}
                searchPhrase={decodedSearchPhraseQuery}
                infinityScroll={true}
                digitalDataOrigin="search"
                showSideButtons={breakpoints.max.SM}
              />
            </div>
            {products!.length > 0 && (
              <div ref={viewMoreRef}>
                <InfinityScrollButton
                  currentlyViewing={Math.min(queryState.page * infinityScrollPageSize, hits)}
                  maxViewing={hits}
                  loading={loading}
                  onClick={() =>
                    setQueryState(
                      {
                        page: (queryState.page + 1).toString(),
                      },
                      false
                    )
                  }
                />
              </div>
            )}
            <ScrollToResults
              productListForwardRef={productListRef as React.RefObject<HTMLElement>}
              filterForwardRef={filterRef as React.RefObject<HTMLElement>}
              totalResults={productsToShow.length}
              idToScrollTo="filter-product-list"
            />
          </div>
        </div>
        {!!secondaryList?.length && (
          <SearchSecondaryList
            title={translate('Search_Page_Secondary_List_Title')}
            list={secondaryList}
          />
        )}
      </div>
    </div>
  )
}

export default SearchPage
